import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert } from '@mui/material';

interface Customer {
  customer_id: string;
  name: string;
  contact_email: string;
  contact_phone: string | null;
  address: any | null;
  logo_url: string | null;
  status: 'active' | 'suspended' | 'inactive';
  distributor_direct: boolean;
  created_at: string;
  reseller?: {
    name: string;
    reseller_id: string;
  } | null;
}

interface Subscription {
  subscription_id: string;
  status: string;
  product: {
    name: string;
    product_id: string;
  };
  start_date: string;
  end_date: string | null;
  quantity: number;
}

export default function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, hasRole, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication and authorization
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    // Only distributors, resellers (for their customers), and the customer themselves can view
    if (!authLoading && user && !hasRole(['distributor', 'reseller', 'customer'])) {
      setError('You do not have permission to view this customer');
      return;
    }

    async function fetchCustomerData() {
      if (!customerId) return;
      
      try {
        setLoading(true);
        
        // Fetch customer details
        let query = supabase
          .from('customers')
          .select(`
            *,
            reseller:resellers (name, reseller_id)
          `)
          .eq('customer_id', customerId)
          .single();
        
        // Apply RLS filtering
        // Supabase RLS will handle permissions based on JWT
          
        const { data: customerData, error: customerError } = await query;
        
        if (customerError) {
          throw new Error(customerError.message);
        }
        
        if (!customerData) {
          throw new Error('Customer not found');
        }
        
        setCustomer(customerData as Customer);
        
        // Fetch subscriptions for this customer
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('subscriptions')
          .select(`
            subscription_id,
            status,
            product:products (name, product_id),
            start_date,
            end_date,
            quantity
          `)
          .eq('customer_id', customerId);
          
        if (subscriptionError) {
          throw new Error(subscriptionError.message);
        }
        
        setSubscriptions(subscriptionData as Subscription[]);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error fetching customer data:', err);
      } finally {
        setLoading(false);
      }
    }

    if (!authLoading) {
      fetchCustomerData();
    }
  }, [customerId, user, hasRole, navigate, authLoading]);

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box m={3}>
        <Alert severity="info">Customer not found</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Customer Details
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6}}>
            <Typography variant="h5">{customer.name}</Typography>
            <Box mt={2}>
              <Typography variant="subtitle1" color="text.secondary">
                Status: <Chip 
                  label={customer.status} 
                  color={customer.status === 'active' ? 'success' : 'error'} 
                  size="small" 
                />
              </Typography>
              <Typography variant="body1">Email: {customer.contact_email}</Typography>
              {customer.contact_phone && (
                <Typography variant="body1">Phone: {customer.contact_phone}</Typography>
              )}
              <Typography variant="body2" color="text.secondary" mt={1}>
                Customer since: {new Date(customer.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6}}>
            <Typography variant="subtitle1" color="text.secondary">
              {customer.distributor_direct ? 'Direct Customer' : 'Reseller Customer'}
            </Typography>
            {!customer.distributor_direct && customer.reseller && (
              <Typography variant="body1">
                Reseller: {customer.reseller.name}
              </Typography>
            )}
            
            {hasRole('distributor') && (
              <Box mt={2}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => navigate(`/customers/${customerId}/edit`)}
                >
                  Edit Customer
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
      
      <Typography variant="h5" gutterBottom>
        Subscriptions
      </Typography>
      
      {subscriptions.length === 0 ? (
        <Alert severity="info">No subscriptions found for this customer</Alert>
      ) : (
        <Grid container spacing={2}>
          {subscriptions.map((subscription) => (
            <Grid size={{ xs: 12, md: 6, lg: 4}} key={subscription.subscription_id}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6">{subscription.product?.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: <Chip 
                    label={subscription.status} 
                    color={subscription.status === 'active' ? 'success' : 'default'} 
                    size="small" 
                  />
                </Typography>
                <Typography variant="body2">
                  Quantity: {subscription.quantity}
                </Typography>
                <Typography variant="body2">
                  Start Date: {new Date(subscription.start_date).toLocaleDateString()}
                </Typography>
                {subscription.end_date && (
                  <Typography variant="body2">
                    End Date: {new Date(subscription.end_date).toLocaleDateString()}
                  </Typography>
                )}
                <Box mt={2}>
                  <Button 
                    variant="text" 
                    size="small"
                    onClick={() => navigate(`/subscriptions/${subscription.subscription_id}`)}
                  >
                    View Details
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      
      {hasRole(['distributor', 'reseller']) && (
        <Box mt={3}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate(`/customers/${customerId}/add-subscription`)}
          >
            Add Subscription
          </Button>
        </Box>
      )}
    </Box>
  );
}